import React, { useState } from "react";
import { Capacitor } from "@capacitor/core";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonPage,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import _ from "lodash";
import { HeaderTitle } from "parafolio-components";
import { useHistory, useParams } from "react-router";
import { useRecoilStateLoadable } from "recoil";
import { Icon_Radio_Active, Icon_Radio_Grey } from "@assets/images";
import * as DatabaseService from "@controllers/DatabaseService";
import DataController from "@controllers/DataController";
import { useProgramData } from "@hooks/data/useProgramData";
import { IUser, IUserProgramRoles, ProgramRole } from "@interfaces/Interfaces";
import { userAtom } from "@state/State";
import { useHidingTabBar } from "@hooks/tabs/useHidingTabBar";
import ContentContainer from "@components/common/ContentContainer";

function RoleAndTeamSetup() {
  const history = useHistory();

  const params = useParams<{ programId: string }>();

  useHidingTabBar();
  const { userPrograms } = useProgramData();

  const program = userPrograms.find((p) => p.ID === params.programId)!;

  const [user, setUser] = useRecoilStateLoadable<IUser | null>(userAtom);

  const [selectedRole, setSelectedRole] = useState(getInitialRole());
  const [selectedTeam, setSelectedTeam] = useState(getInitialTeam());
  const [selectedCourseId, setSelectedCourseId] = useState(getInitialCourseId());
  const [saving, setSaving] = useState(false);

  let disableSaveButton = saving || selectedRole === "" || selectedTeam === "";
  if (program.CourseIds) {
    disableSaveButton = saving || selectedRole === "" || selectedTeam === "" || selectedCourseId === "";
  }

  function getInitialRole() {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role.programRoles.length) {
        return role.programRoles[0];
      }
    }

    return "";
  }

  function getInitialTeam() {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role?.team) {
        return role.team;
      }
    }

    return "";
  }

  function getInitialCourseId() {
    if (user.state === "hasValue" && user.contents) {
      const role = user.contents.programRoles?.find((role) => role.programID === program.ID);
      if (role && role?.courseId) {
        return role.courseId;
      }
    }

    return "";
  }

  async function saveDetails() {
    setSaving(true);

    try {
      if (user.state === "hasValue" && user.contents) {
        const userToSave = _.cloneDeep(user.contents);

        const newData: IUserProgramRoles = {
          programName: program.Name,
          programID: program.ID,
          programRoles: [selectedRole],
          team: selectedTeam,
          courseId: selectedCourseId,
        };

        const userRoles = userToSave.programRoles || [];
        const index = userRoles.findIndex((item) => item.programID === program.ID);

        if (index === -1) {
          userRoles.push(newData);
        } else {
          userRoles.splice(index, 1, newData);
        }

        userToSave.programRoles = userRoles;

        await DatabaseService.updateUserDetails(userToSave, {
          programRoles: userToSave.programRoles,
        });

        setUser(userToSave);

        setTimeout(() => {
          history.goBack();
        }, 500);
      }
    } catch (error) {
      console.log(error);
      setSaving(false);
      window.alert("An error occurred when saving details");
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref="/dashboard"
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Your Details"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background whiteBackground">
        <ContentContainer
          className="!pt-0 pb-4"
          style={{ paddingTop: 0, alignSelf: "center" }}
        >
          <div className="mx-auto max-w-app-column pt-4">
            <div className="programAddCertificateTitle">{"Set up your details"}</div>
            <div className="accountCardSubTitle">{program.DetailsDescription}</div>
            <div className="editRolesHeader mt-8">{"Your role"}</div>
            {program.Roles?.map((role: ProgramRole) => {
              return (
                <IonItem
                  button
                  detail={false}
                  key={role.ID}
                  className="detailsRoleCheckboxContainer"
                  onClick={() => setSelectedRole(role.Name)}
                >
                  <div className="evidencePartsCheckboxContainerInner">
                    <div className="programRoleCheckboxContainerInner">
                      <div
                        className={`evidencePartsCheckboxLabel ${selectedRole === role.Name ? "evidencePartsCheckboxLabelSelected" : ""}`}
                      >
                        {role.Name}
                      </div>
                      {role.Description && <div className="programRoleDescription">{role.Description}</div>}
                    </div>
                    <img
                      className="evidencePartsRadioCheckbox"
                      src={selectedRole === role.Name ? Icon_Radio_Active : Icon_Radio_Grey}
                    />
                  </div>
                </IonItem>
              );
            })}
            <div className="editRolesHeader mt-8">{"Your team"}</div>
            {program.Teams?.map((team: string) => {
              return (
                <IonItem
                  button
                  detail={false}
                  key={team}
                  className="detailsRoleCheckboxContainer"
                  onClick={() => setSelectedTeam(team)}
                >
                  <div className="evidencePartsCheckboxContainerInner">
                    <div className="programRoleCheckboxContainerInner">
                      <div
                        className={`evidencePartsCheckboxLabel ${selectedTeam === team ? "evidencePartsCheckboxLabelSelected" : ""}`}
                      >
                        {team}
                      </div>
                    </div>
                    <img
                      className="evidencePartsRadioCheckbox"
                      src={selectedTeam === team ? Icon_Radio_Active : Icon_Radio_Grey}
                    />
                  </div>
                </IonItem>
              );
            })}
            {program.CourseIds && (
              <>
                <div className="editRolesHeader mt-8">{"Your course"}</div>
                <select
                  className="custom-select-arrow mt-1.5 w-full cursor-pointer appearance-none truncate rounded-md border border-gray-300 bg-gray-200 px-3 py-2 pr-10 leading-tight text-grey-80 focus:border-blue-500 focus:outline-none"
                  value={selectedCourseId}
                  onChange={(event) => setSelectedCourseId(event.target.value)}
                >
                  <option
                    value=""
                    disabled
                  >
                    {"Choose an option"}
                  </option>
                  {program.CourseIds?.map((course) => (
                    <option
                      key={course}
                      value={course}
                    >
                      {course}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </ContentContainer>
      </IonContent>
      <IonFooter className="programDetailsFooter">
        <div className="programDetailsSaveContainer">
          <IonButton
            mode="ios"
            disabled={disableSaveButton}
            className={`programDetailsSaveButton ${disableSaveButton && "reflectionsSaveButtonDisabled"}`}
            onClick={() => saveDetails()}
          >
            {saving ? <IonSpinner className="reflectionSaveSpinner" /> : <div>{"Confirm"}</div>}
          </IonButton>
        </div>
      </IonFooter>
    </IonPage>
  );
}

export default RoleAndTeamSetup;
